import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Home,
  TrendingUp,
  FileText,
  Users,
  BarChart3,
  Mail,
  ClipboardList,
  Calculator,
  Database,
  Megaphone,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function ServicesPage() {
  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="services-page">
      <SEO
        title="Services"
        description="Explore Momentum VA's real estate support services — tenancy administration, compliance management, application processing, lease preparation, appraisal administration, marketing co-ordination, and CRM management for Australian offices."
        path="/services"
      />
      {/* Hero Section */}
      <section className="relative bg-[#0B1C3E] py-20 lg:py-28" data-testid="services-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-[#5D6D35] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-[#2A9D8F] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6 font-['Outfit']">
              Comprehensive Real Estate Support
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              From property management to sales support, our trained virtual assistants 
              handle the operational tasks that keep your business running smoothly.
            </p>
          </div>
        </div>
      </section>

      {/* Property Management Services */}
      <section className="py-20 md:py-28 bg-white" data-testid="pm-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <div className="inline-flex items-center px-4 py-2 bg-[#5D6D35]/10 rounded-full mb-6">
                <Home className="w-5 h-5 text-[#5D6D35] mr-2" />
                <span className="text-[#5D6D35] font-semibold text-sm">Property Management</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mb-6 font-['Outfit']">
                Property Management Support
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Keep your property management operations running efficiently with dedicated 
                support for all the admin tasks that consume your team's time.
              </p>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h4 className="font-semibold text-[#0B1C3E] mb-4 font-['Outfit']">
                  Perfect for teams who need:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#5D6D35] flex-shrink-0" />
                    <span className="text-slate-700">Efficient tenancy administration</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#5D6D35] flex-shrink-0" />
                    <span className="text-slate-700">Reliable application handling</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#5D6D35] flex-shrink-0" />
                    <span className="text-slate-700">Thorough compliance management</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#5D6D35] flex-shrink-0" />
                    <span className="text-slate-700">Accurate lease preparation</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#5D6D35]/30 hover:shadow-md transition-all group card-hover" data-testid="pm-service-0">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Tenancy Administration</h3>
                    <p className="text-slate-600">End-to-end tenancy administration, from managing tenant records to processing renewals and updating systems.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#5D6D35]/30 hover:shadow-md transition-all group card-hover" data-testid="pm-service-1">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <ClipboardList className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Application Processing</h3>
                    <p className="text-slate-600">Thorough tenant application handling including reference checks, document verification, and compliance checks.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#5D6D35]/30 hover:shadow-md transition-all group card-hover" data-testid="pm-service-2">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Calculator className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Compliance Management</h3>
                    <p className="text-slate-600">Ensuring your property portfolio meets all regulatory requirements, tracking deadlines and maintaining compliance documentation.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#5D6D35]/30 hover:shadow-md transition-all group card-hover" data-testid="pm-service-3">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Database className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Lease Preparation</h3>
                    <p className="text-slate-600">Preparing and managing lease documents, ensuring accuracy and timely delivery for all tenancy agreements.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sales & Marketing Services */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="sales-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6 order-2 lg:order-1">
              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#2A9D8F]/30 hover:shadow-md transition-all group card-hover" data-testid="sales-service-0">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#2A9D8F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Appraisal Administration</h3>
                    <p className="text-slate-600">Managing appraisal workflows, preparing materials, and coordinating schedules for your sales team.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#2A9D8F]/30 hover:shadow-md transition-all group card-hover" data-testid="sales-service-1">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#2A9D8F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">CRM Management</h3>
                    <p className="text-slate-600">Keeping your CRM organised, updating contacts, tracking leads, and managing follow-up sequences.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#2A9D8F]/30 hover:shadow-md transition-all group card-hover" data-testid="sales-service-2">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#2A9D8F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Pre List and List Admin</h3>
                    <p className="text-slate-600">Handling all pre-listing and listing administration, from document preparation to portal uploads and coordination.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#2A9D8F]/30 hover:shadow-md transition-all group card-hover" data-testid="sales-service-3">
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#2A9D8F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Megaphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Marketing Coordination</h3>
                    <p className="text-slate-600">Coordinating marketing campaigns, social media scheduling, and promotional material distribution.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-28 order-1 lg:order-2">
              <div className="inline-flex items-center px-4 py-2 bg-[#2A9D8F]/10 rounded-full mb-6">
                <TrendingUp className="w-5 h-5 text-[#2A9D8F] mr-2" />
                <span className="text-[#2A9D8F] font-semibold text-sm">Sales & Marketing</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mb-6 font-['Outfit']">
                Sales & Marketing Support
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Help your sales team focus on relationships and closings while we handle 
                the administrative tasks that support their success.
              </p>

              <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                <h4 className="font-semibold text-[#0B1C3E] mb-4 font-['Outfit']">
                  Perfect for teams who need:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                    <span className="text-slate-700">Streamlined appraisal administration</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                    <span className="text-slate-700">Organised pre list and list admin</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                    <span className="text-slate-700">Consistent marketing co-ordination</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                    <span className="text-slate-700">Organised CRM and lead tracking</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Deliver */}
      <section className="py-20 md:py-28 bg-white" data-testid="delivery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Our Promise
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              How We Deliver Results
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Every service we provide comes with our commitment to quality, 
              consistency, and genuine partnership.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Process-Driven</h3>
              <p className="text-slate-600">Every task follows documented procedures to ensure consistent quality and outcomes.</p>
            </div>

            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Fully Managed</h3>
              <p className="text-slate-600">Our team leaders oversee all work, so you don't have to manage another employee.</p>
            </div>

            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Scalable</h3>
              <p className="text-slate-600">Start small and expand support as your business grows—flexibility built in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0B1C3E]" data-testid="services-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss which services would best support your real estate office. 
            Book a free discovery call to explore the possibilities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg"
                data-testid="services-cta-book-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                Book a Discovery Call
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                data-testid="services-cta-how-works"
              >
                See How It Works
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
